import React, { useState, useEffect, useRef, memo } from "react";
import "./ApplicantCard.css";
import { galleryService } from "../services/galleryService";

// Global cache for image URLs
const imageUrlCache = new Map();

// Simple placeholder image
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='130' height='170' viewBox='0 0 130 170'%3E%3Crect width='130' height='170' fill='%23e0e0e0'/%3E%3Ctext x='65' y='95' text-anchor='middle' fill='%23999' font-size='28'%3E📷%3C/text%3E%3C/svg%3E";

// ✅ Use React.memo to prevent unnecessary re-renders
const ApplicantCard = memo(({ applicant, onVideoClick, isVideoLoading }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const hasFetched = useRef(false);
  const isMounted = useRef(true);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  const salary = applicant.salary || 1000;

  // Dynamic skills from applicant data
  const getSkills = () => {
    const skills = [];
    if (applicant.ironing) skills.push("👕 Ironing");
    if (applicant.cooking && applicant.cooking.toUpperCase() === "YES") {
      skills.push("🍳 Cooking");
    }
    if (applicant.arabicCooking) skills.push("🥘 Arabic Cooking");
    if (applicant.sewing) skills.push("🧵 Sewing");
    if (applicant.babySitting) skills.push("👶 Babysitting");
    if (applicant.cleaning) skills.push("🧹 Cleaning");
    if (applicant.washing) skills.push("🧺 Washing");
    if (applicant.childCare) skills.push("👧 Child Care");
    return skills.slice(0, 5); // Show max 5 skills
  };

  const skills = getSkills();

  const getFlag = () => {
    if (applicant.currentNationality === "Ethiopia") return "🇪🇹";
    if (applicant.currentNationality === "Philippines") return "🇵🇭";
    if (applicant.currentNationality === "India") return "🇮🇳";
    if (applicant.currentNationality === "Sri Lanka") return "🇱🇰";
    if (applicant.currentNationality === "Nepal") return "🇳🇵";
    return "🌍";
  };

  // Check if video exists (using streaming URL or hasVideo flag)
  const hasVideo = !!(applicant.videoUrl || applicant.hasVideo);

  // Check if video URL is a streaming endpoint
  const isStreamingUrl = (url) => {
    return url && url.includes('/api/video/stream/');
  };

  // Log video URL for debugging
  useEffect(() => {
    if (applicant.videoUrl) {
      console.log(`🎬 Applicant ${applicant.fullName} video URL:`, applicant.videoUrl);
      console.log(`📹 Has video: ${hasVideo}, Is streaming: ${isStreamingUrl(applicant.videoUrl)}`);
    }
  }, [applicant.videoUrl, applicant.fullName, hasVideo]);

  // ✅ Setup Intersection Observer for lazy loading
  useEffect(() => {
    if (!imgRef.current) return;

    const options = {
      root: null,
      rootMargin: '200px', // Load 200px before visible
      threshold: 0.01
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Disconnect after first load to free resources
          if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
          }
        }
      });
    }, options);

    observerRef.current.observe(imgRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  // ✅ Load image only when visible
  useEffect(() => {
    isMounted.current = true;

    // Skip if already fetched or no photo key
    if (hasFetched.current || !applicant.photoKey) {
      setImageLoading(false);
      return;
    }

    // Only load if visible
    if (!isVisible) {
      setImageLoading(true);
      return;
    }

    // Check cache
    if (imageUrlCache.has(applicant.photoKey)) {
      const cached = imageUrlCache.get(applicant.photoKey);
      setImageUrl(cached);
      setImageLoading(false);
      hasFetched.current = true;
      return;
    }

    hasFetched.current = true;

    const loadImage = async () => {
      try {
        // Use the URL from the API response directly (already generated)
        if (applicant.photoUrl) {
          imageUrlCache.set(applicant.photoKey, applicant.photoUrl);
          setImageUrl(applicant.photoUrl);
        } else {
          // Fallback: fetch using service
          const url = await galleryService.getImageUrl(applicant.photoKey);
          imageUrlCache.set(applicant.photoKey, url);
          setImageUrl(url);
        }
      } catch (error) {
        imageUrlCache.set(applicant.photoKey, null);
        console.warn('Failed to load image for:', applicant.fullName);
      } finally {
        if (isMounted.current) {
          setImageLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted.current = false;
    };
  }, [applicant.photoKey, applicant.photoUrl, applicant.fullName, isVisible]);

  const handleVideoClick = (e) => {
    e.stopPropagation();
    if (hasVideo && applicant.videoUrl) {
      console.log(`🎬 Playing video for ${applicant.fullName}:`, applicant.videoUrl);
      onVideoClick(applicant.videoUrl);
    } else {
      console.warn(`⚠️ No video available for ${applicant.fullName}`);
    }
  };

  const handleViewFile = (e) => {
    e.stopPropagation();
    // Open applicant details modal or navigate
    if (onVideoClick) {
      onVideoClick(applicant.id); // Or handle view file
    }
  };

  const handleInquiry = (e) => {
    e.stopPropagation();
    alert(`Inquiry sent for ${applicant.fullName}`);
    // You can integrate with your inquiry system here
  };

  const finalImageUrl = imageUrl || PLACEHOLDER_IMAGE;

  return (
    <div className="applicant-card">
      <div className="card-header">
        <span className="agency-name">
          {applicant.agentName || "Agency"}
        </span>
        <span className="job-title">
          {applicant.occupation || "Domestic Worker"}
        </span>
      </div>

      <div className="card-body">
        <div className="photo-column">
          {/* Video Badge - shows streaming indicator */}
          <div 
            className={`video-badge ${!hasVideo ? 'video-disabled' : ''}`} 
            onClick={hasVideo ? handleVideoClick : undefined}
            style={!hasVideo ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
            title={hasVideo ? 'Click to play video' : 'No video available'}
          >
            {isVideoLoading ? '⏳ Loading...' : (hasVideo ? '🎥 Video' : '🚫 No Video')}
          </div>

          {imageLoading && (
            <div className="loading-overlay">
              <div className="spinner-small"></div>
            </div>
          )}
          
          {/* ✅ Image with lazy loading and ref for Intersection Observer */}
          <img
            ref={imgRef}
            src={finalImageUrl}
            alt={applicant.fullName}
            className={`applicant-photo ${imageLoading ? 'loading' : 'loaded'}`}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.target.src = PLACEHOLDER_IMAGE;
              console.warn(`⚠️ Image failed to load for: ${applicant.fullName}`);
            }}
          />

          <div className="ref-number">
            {applicant.referenceNo || "-"}
          </div>
        </div>

        <div className="info-column">
          <h3 className="candidate-name">
            {applicant.fullName}
          </h3>

          <div className="info-row">
            <span>Nationality</span>
            <strong>{applicant.currentNationality || "-"}</strong>
          </div>

          <div className="info-row">
            <span>Religion</span>
            <strong>{applicant.religion || "-"}</strong>
          </div>

          <div className="info-row">
            <span>Age</span>
            <strong>{applicant.age ? `${applicant.age} Yr` : "-"}</strong>
          </div>

          <div className="info-row">
            <span>M.Status</span>
            <strong>{applicant.maritalStatus || "-"}</strong>
          </div>

          <div className="info-row">
            <span>Salary</span>
            <strong>{salary ? `${salary} SAR` : "-"}</strong>
          </div>

          <div className="info-row">
            {<span className="country-flag">
                {getFlag()}
                </span>}
                <span className={`${applicant.experienceInAbroad > 0 ? 'experience' : 'non-experience'} experience-text`}>
                    {applicant.experienceInAbroad > 0 ? `Experienced (${applicant.experienceInAbroad} Yrs)` : "No experience"}
                </span>
          </div>
        </div>
      </div>

      <div className="action-row">
        <button className="view-btn" onClick={handleViewFile}>
          👁 View File
        </button>        
      </div>

      {skills.length > 0 && (
        <div className="skills-row">
          {skills.map((skill, index) => (
            <span key={index} className="skill-badge">
              {skill}
            </span>
          ))}
        </div>
      )}

      <button className="inquiry-btn" onClick={handleInquiry}>
        💬 Click here to inquire
      </button>
    </div>
  );
});

// ✅ Add display name for debugging
ApplicantCard.displayName = 'ApplicantCard';

export default ApplicantCard;